import { useState } from 'react';
import {
  useAuth,
  useListFetch,
  useModal,
  useRequest,
} from '../../../../../hooks';
import { notify } from '../../../../../app';
import {
  createBaseKnowledge,
  deleteKnowledgeBase,
  downloadKnowledgeBase,
  extendKnowledgeByUrl,
  getAllKnowledges,
  renameKnowledgeBase,
} from '../../../../../api';
import type { BaseKnowledgePayload } from '../../../../../api';
import { handleDownloadFile } from '../../KnowledgeSourceList/utils';

export const useKnowledgeManagement = () => {
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isOpenedShowMetadataModal,
    onClose: onCloseShowMetadataModal,
    onOpen: onOpenShowMetadataModal,
  } = useModal();
  const {
    isOpened: isOpenedDeleteModal,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useModal();
  const {
    isOpened: isOpenedExtendByUrlModal,
    onClose: onCloseExtendByUrlModal,
    onOpen: onOpenExtendByUrlModal,
  } = useModal();

  const { user } = useAuth();
  const userId = user?.identities?.[0]?.user_id || '';
  const [downloadingKnowledgeBase, setDownloadingKnowledgeBase] =
    useState(false);

  const { nodes, filters, setFilters, setSort, intializing, refresh, ...rest } =
    useListFetch(
      async params =>
        await getAllKnowledges({
          includeInactive: false,
          page: (params?.pagination?.page ?? 1) + 1,
          name: params?.filters?.name,
          limit: 20,
          debug: false,
        }),
      {
        deps: [],
        resultKey: 'tables',
        withQueryParams: true,
      }
    );

  const [handleCreateBaseKnowledge, creatingBaseKnowledge] = useRequest(
    (_signal, payload: BaseKnowledgePayload & { metadata_fields: string }) =>
      createBaseKnowledge({
        ...payload,
        user_id: userId,
        metadata_fields: payload.metadata_fields
          ? JSON.parse(payload.metadata_fields)
          : null,
      }),
    {
      onSuccess: () => {
        notify.success('The base knowledge has been created successfully');
        refresh();
        onClose?.();
      },
      onError: () => {
        notify.error('Failed to create the base knowledge');
      },
      deps: [],
    }
  );

  const [handleRenameBaseKnowledge, renamingBaseKnowledge] = useRequest(
    (_signal, tableId: string, newName: string, metadata_fields: string) =>
      renameKnowledgeBase(tableId, {
        new_name: newName,
        metadata_fields: metadata_fields ? JSON.parse(metadata_fields) : null,
      }),
    {
      onSuccess: () => {
        notify.success('The base knowledge renamed successfully');
        refresh();
        onClose?.();
      },
      onError: () => {
        notify.error('Failed to create the base knowledge');
      },
      deps: [],
    }
  );

  const [handleDeleteKnowledgeBase, deletingKnowledgeBase] = useRequest(
    (_signal, id: string) => deleteKnowledgeBase(id),
    {
      onSuccess: () => {
        notify.success('The knowledge base deleted successfully');
        refresh();
        onCloseDeleteModal?.();
      },
      onError: () => {
        notify.error('Failed to delete the knowledge base');
      },
      deps: [],
    }
  );

  const [handleExtendKnowledgeByUrl, extendingKnowledgeByUrl] = useRequest(
    (_signal, knowledgeBaseId: string, url: string) =>
      extendKnowledgeByUrl({ knowledgeBaseId, url }),
    {
      onSuccess: () => {
        notify.success('Knowledge base extended successfully');
        onCloseExtendByUrlModal?.();
      },
      onError: () => {
        notify.error('Failed to extend the knowledge base by URL');
      },
      deps: [],
    }
  );

  const handleDownloadKnowledgeBase = async (
    knowledgeBaseId: string,
    knowledgeBaseName: string
  ) => {
    await handleDownloadFile(
      () => downloadKnowledgeBase(knowledgeBaseId),
      knowledgeBaseName || 'knowledge-base',
      setDownloadingKnowledgeBase,
      'Knowledge base downloaded successfully',
      'Failed to download knowledge base'
    );
  };

  return {
    setSort,
    filters,
    setFilters,
    intializing,
    isOpened,
    onClose,
    onOpen,
    knowlegeList: nodes ?? [],
    handleRenameBaseKnowledge,
    renamingBaseKnowledge,
    handleCreateBaseKnowledge,
    creatingBaseKnowledge,
    handleDeleteKnowledgeBase,
    deletingKnowledgeBase,
    handleDownloadKnowledgeBase,
    downloadingKnowledgeBase,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    isOpenedShowMetadataModal,
    onCloseShowMetadataModal,
    onOpenShowMetadataModal,
    isOpenedExtendByUrlModal,
    onCloseExtendByUrlModal,
    onOpenExtendByUrlModal,
    handleExtendKnowledgeByUrl,
    extendingKnowledgeByUrl,
    ...rest,
  };
};
